import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, TrendingUpIcon, UsersIcon, FolderIcon, DatabaseIcon, SettingsIcon, LayoutDashboardIcon, ChevronRightIcon, CommandIcon } from 'lucide-react';
import { seedData } from '../data/seedData';
interface CommandPaletteProps {
  onNavigate: (view: string, metadata?: any) => void;
  onClose: () => void;
}
interface Command {
  id: string;
  label: string;
  category: string;
  icon: React.ReactNode;
  action: () => void;
  keywords: string[];
}
export function CommandPalette({
  onNavigate,
  onClose
}: CommandPaletteProps) {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const commands: Command[] = [
  // Navigation
  {
    id: 'nav-accounts',
    label: 'Go to Accounts',
    category: 'Navigation',
    icon: <LayoutDashboardIcon className="w-4 h-4" data-id="element-463" />,
    action: () => onNavigate('accounts'),
    keywords: ['accounts', 'dashboard', 'home']
  }, {
    id: 'nav-spaces',
    label: 'Go to Spaces Explorer',
    category: 'Navigation',
    icon: <DatabaseIcon className="w-4 h-4" data-id="element-464" />,
    action: () => onNavigate('spaces'),
    keywords: ['spaces', 'knowledge', 'documents']
  }, {
    id: 'nav-analytics',
    label: 'Go to Analytics',
    category: 'Navigation',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-465" />,
    action: () => onNavigate('analytics'),
    keywords: ['analytics', 'metrics', 'reports']
  }, {
    id: 'nav-people',
    label: 'Go to People',
    category: 'Navigation',
    icon: <UsersIcon className="w-4 h-4" data-id="element-466" />,
    action: () => onNavigate('people'),
    keywords: ['people', 'team', 'contacts']
  }, {
    id: 'nav-orders',
    label: 'Go to Orders',
    category: 'Navigation',
    icon: <FolderIcon className="w-4 h-4" data-id="element-467" />,
    action: () => onNavigate('orders'),
    keywords: ['orders', 'projects', 'jobs']
  }, {
    id: 'nav-settings',
    label: 'Go to Settings',
    category: 'Navigation',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-468" />,
    action: () => onNavigate('settings'),
    keywords: ['settings', 'preferences', 'config']
  },
  // Accounts
  ...seedData.accounts.slice(0, 5).map(account => ({
    id: `account-${account.id}`,
    label: `Open ${account.name}`,
    category: 'Accounts',
    icon: <div className="w-4 h-4 rounded bg-white/10 flex items-center justify-center text-[8px]" data-id="element-469">
          {account.name.charAt(0)}
        </div>,
    action: () => {
      onNavigate('accounts', {
        accountId: account.id
      });
    },
    keywords: [account.name.toLowerCase(), account.industry.toLowerCase()]
  }))];
  const filteredCommands = search ? commands.filter(cmd => cmd.label.toLowerCase().includes(search.toLowerCase()) || cmd.keywords.some(kw => kw.includes(search.toLowerCase()))) : commands;
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {} as Record<string, Command[]>);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => prev < filteredCommands.length - 1 ? prev + 1 : prev);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action();
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredCommands, selectedIndex, onClose]);
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]" data-id="element-470" />
      {/* Palette */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: -20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: -20
    }} className="fixed top-32 left-1/2 -translate-x-1/2 w-full max-w-2xl z-[101]" data-id="element-471">
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden" data-id="element-472">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-800" data-id="element-473">
            <SearchIcon className="w-5 h-5 text-slate-400" data-id="element-474" />
            <input type="text" value={search} onChange={e => {
            setSearch(e.target.value);
            setSelectedIndex(0);
          }} placeholder="Type a command or search..." className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none text-base" autoFocus data-id="element-475" />
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-800/60 border border-slate-700/50" data-id="element-476">
              <CommandIcon className="w-3 h-3 text-slate-400" data-id="element-477" />
              <span className="text-xs text-slate-400 font-mono" data-id="element-478">K</span>
            </div>
          </div>
          {/* Commands List */}
          <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-479">
            {Object.keys(groupedCommands).length === 0 ? <div className="px-5 py-8 text-center text-slate-400 text-sm" data-id="element-480">
                No commands found
              </div> : Object.entries(groupedCommands).map(([category, cmds]) => <div key={category} className="py-2" data-id="element-481">
                  <div className="px-5 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-482">
                    {category}
                  </div>
                  {cmds.map((cmd, idx) => {
              const globalIndex = filteredCommands.indexOf(cmd);
              const isSelected = globalIndex === selectedIndex;
              return <button key={cmd.id} onClick={() => {
                cmd.action();
                onClose();
              }} onMouseEnter={() => setSelectedIndex(globalIndex)} className={`w-full flex items-center gap-3 px-5 py-3 transition-colors ${isSelected ? 'bg-blue-600/20 text-white' : 'text-slate-300 hover:bg-slate-800/60'}`} data-id="element-483">
                        <div className={`${isSelected ? 'text-blue-400' : 'text-slate-500'}`} data-id="element-484">
                          {cmd.icon}
                        </div>
                        <span className="flex-1 text-left text-sm font-medium" data-id="element-485">
                          {cmd.label}
                        </span>
                        <ChevronRightIcon className={`w-4 h-4 ${isSelected ? 'text-blue-400' : 'text-slate-600'}`} data-id="element-486" />
                      </button>;
            })}
                </div>)}
          </div>
          {/* Footer */}
          <div className="px-5 py-3 border-t border-slate-800 bg-slate-950/50 flex items-center justify-between text-xs text-slate-500" data-id="element-487">
            <div className="flex items-center gap-4" data-id="element-488">
              <span data-id="element-489">
                <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400 font-mono" data-id="element-490">
                  ↑↓
                </kbd>{' '}
                Navigate
              </span>
              <span data-id="element-491">
                <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400 font-mono" data-id="element-492">
                  ↵
                </kbd>{' '}
                Select
              </span>
            </div>
            <span data-id="element-493">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400 font-mono" data-id="element-494">
                ESC
              </kbd>{' '}
              Close
            </span>
          </div>
        </div>
      </motion.div>
    </>;
}